import { useState, useMemo } from 'react';
import {
    Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    Legend, ReferenceLine, ComposedChart, Line, Cell
} from 'recharts';
import {
    Server, Zap, Snowflake, AlertTriangle, CheckCircle2,
    DollarSign, Leaf, Plus, Minus,
    Network, ShieldAlert, Info, Gauge, Layers
} from 'lucide-react';

/* -----------------------------------------------------------------------
 * Baseline — matches values in EnergyOverview / CarbonEmissionOverview
 * 16-rack small DC, ~88 kW IT, PUE 1.42, 89% renewable mix
 * --------------------------------------------------------------------- */
const BASE = {
    racks: 16,
    avgKwPerRack: 5.5,
    itLoadKw: 88,
    pue: 1.42,
    facilityKw: 124.96,
    upsTotalKw: 160,            // 2 × 100 kVA @ 0.9 PF, derated for N+1
    crackUnits: 4,              // 4 × 10-ton CRAC, N+1
    crackCapacityTons: 40,
    itCoolingTons: 25.0,        // 88 kW × 0.2843
    floorSqftTotal: 800,
    floorSqftUsed: 480,
    networkPorts: 32,
    networkPortsUsed: 31,
    networkPortsTotal: 48,      // ToR pair has spare ports
    monthlyKwh: 89971,
    costPerKwh: 0.11,
    co2PerKwh: 0.097,           // kg CO2e / kWh (89% renewable)
    monthlyCost: 9897,
    monthlyCo2: 8.73,           // tCO2e
    renewablePct: 89,
};

const PROFILES = {
    standard: {
        name: 'Standard 1U/2U',
        kwPerRack: 5.0,
        sub: 'Web/App servers · ~32 nodes/rack',
        capexPerRack: 12000,
        color: '#60a5fa',
    },
    highdensity: {
        name: 'High Density',
        kwPerRack: 8.5,
        sub: 'Storage + DB · ~24 nodes/rack',
        capexPerRack: 18000,
        color: '#a78bfa',
    },
    gpu: {
        name: 'GPU / AI',
        kwPerRack: 14.0,
        sub: '8× H100-class accelerators',
        capexPerRack: 38000,
        color: '#f87171',
    },
} as const;
type ProfileKey = keyof typeof PROFILES;

const KW_TO_TON = 0.2843;
const KW_TO_BTU = 3412;
const SQFT_PER_NEW_RACK = 28;     // rack footprint + share of aisle
const PORTS_PER_NEW_RACK = 2;     // dual ToR
const fmt = (n: number, d = 0) =>
    n.toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });

/* -----------------------------------------------------------------------
 * Animations
 * --------------------------------------------------------------------- */
const animStyles = `
@keyframes sim-flow-pwr { from { stroke-dashoffset: 24; } to { stroke-dashoffset: 0; } }
@keyframes sim-flow-cool { from { stroke-dashoffset: 0; } to { stroke-dashoffset: 24; } }
@keyframes sim-spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }
@keyframes sim-pulse { 0%,100% { opacity: .45; } 50% { opacity: 1; } }
@keyframes sim-heat { 0% { opacity: 0; transform: translateY(6px); } 50% { opacity: .9; } 100% { opacity: 0; transform: translateY(-12px); } }
@keyframes sim-wave { 0% { transform: translateX(-4px); } 100% { transform: translateX(4px); } }
.sim-pwr-line { stroke-dasharray: 8 4; animation: sim-flow-pwr .9s linear infinite; }
.sim-cool-line { stroke-dasharray: 6 6; animation: sim-flow-cool 1.4s linear infinite; }
.sim-fan { animation: sim-spin 2.4s linear infinite; transform-origin: 50% 50%; transform-box: fill-box; }
.sim-fan-fast { animation: sim-spin 0.7s linear infinite; transform-origin: 50% 50%; transform-box: fill-box; }
.sim-pulse { animation: sim-pulse 2.4s ease-in-out infinite; }
.sim-wave { animation: sim-wave 1.6s ease-in-out infinite alternate; }
.sim-heat { animation: sim-heat 2.4s ease-out infinite; }
.sim-transition { transition: all .35s cubic-bezier(.4,0,.2,1); }
`;

/* -----------------------------------------------------------------------
 * Custom tooltip (matches existing dashboards)
 * --------------------------------------------------------------------- */
const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
        return (
            <div className="bg-[#1a1d24] border border-[#2e323a] p-3 rounded-lg shadow-xl">
                <p className="text-zinc-400 text-xs mb-2">{label}</p>
                {payload.map((entry: any, i: number) => (
                    <div key={i} className="flex items-center gap-2 text-xs font-semibold" style={{ color: entry.color || entry.fill }}>
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color || entry.fill }} />
                        {entry.name}: {typeof entry.value === 'number' ? fmt(entry.value, 1) : entry.value}
                    </div>
                ))}
            </div>
        );
    }
    return null;
};

/* -----------------------------------------------------------------------
 * Sub-components
 * --------------------------------------------------------------------- */
const MetricCard = ({ icon, label, value, sub, delta, deltaTone }: any) => {
    const tone =
        deltaTone === 'critical' ? 'bg-red-500/15 text-red-400 border-red-500/40' :
            deltaTone === 'warn' ? 'bg-orange-500/15 text-orange-400 border-orange-500/40' :
                deltaTone === 'good' ? 'bg-green-500/15 text-green-400 border-green-500/40' :
                    'bg-zinc-700/30 text-zinc-300 border-zinc-700/50';
    return (
        <div className="bg-[#1a1d24] border border-[#2e323a] rounded-lg p-4 flex flex-col justify-between min-h-[120px] shadow-sm">
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 text-zinc-400">
                    <div className="p-1 rounded bg-zinc-800/60">{icon}</div>
                    <span className="text-[10px] font-bold uppercase tracking-wider">{label}</span>
                </div>
                {delta && (
                    <span className={`text-[9px] px-1.5 py-0.5 rounded border font-bold ${tone}`}>{delta}</span>
                )}
            </div>
            <div>
                <div className="text-2xl font-bold text-white leading-none sim-transition">{value}</div>
                <div className="text-[10px] text-zinc-500 mt-1.5">{sub}</div>
            </div>
        </div>
    );
};

const CapacityBar = ({ icon, label, used, capacity, unit, warnPct = 75, critPct = 90 }: any) => {
    const pct = Math.min(140, (used / capacity) * 100);
    const displayPct = Math.min(100, pct);
    const isCrit = pct >= critPct;
    const isWarn = pct >= warnPct && !isCrit;
    const color = isCrit ? '#ef4444' : isWarn ? '#f97316' : '#22c55e';
    const status = isCrit ? 'CRITICAL' : isWarn ? 'WARN' : 'OK';
    return (
        <div className="bg-[#14161b] border border-[#2e323a] rounded p-3 flex flex-col gap-2">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5 text-zinc-300 text-[11px] font-bold">
                    {icon}
                    {label}
                </div>
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded sim-transition"
                    style={{ background: `${color}22`, color }}>
                    {status}
                </span>
            </div>
            <div className="flex items-baseline justify-between">
                <span className="text-base font-bold text-white sim-transition">{fmt(used, used < 10 ? 1 : 0)}<span className="text-[10px] text-zinc-500 ml-1">/ {fmt(capacity)} {unit}</span></span>
                <span className="text-[10px] font-bold sim-transition" style={{ color }}>{fmt(pct, 1)}%</span>
            </div>
            <div className="relative w-full h-2 bg-[#2e323a] rounded-full overflow-hidden">
                <div className="absolute left-0 top-0 h-full rounded-full sim-transition"
                    style={{ width: `${displayPct}%`, background: `linear-gradient(90deg, ${color}aa, ${color})`, boxShadow: `0 0 10px ${color}66` }} />
                {/* Warn / Crit threshold markers */}
                <div className="absolute top-0 h-full border-l border-orange-500/40" style={{ left: `${warnPct}%` }} />
                <div className="absolute top-0 h-full border-l border-red-500/60" style={{ left: `${critPct}%` }} />
                {pct > 100 && (
                    <div className="absolute top-0 h-full sim-pulse"
                        style={{ left: '100%', width: `${Math.min(40, pct - 100)}%`, background: 'repeating-linear-gradient(45deg, #ef4444aa 0 4px, transparent 4px 8px)' }} />
                )}
            </div>
        </div>
    );
};

const RackTile = ({ kw, kind, idx }: { kw: number; kind: 'existing' | 'new' | 'over'; idx: number }) => {
    const baseColor =
        kind === 'over' ? '#ef4444' :
            kind === 'new' ? '#a78bfa' :
                '#3f6f4f';
    const accent =
        kind === 'over' ? '#ef4444' :
            kind === 'new' ? '#c4b5fd' :
                '#4ade80';
    return (
        <div
            className={`relative sim-transition ${kind === 'new' ? 'sim-pulse' : ''}`}
            style={{ width: 26, height: 56 }}
            title={`Rack ${idx + 1} · ${fmt(kw, 1)} kW`}
        >
            <div
                className="absolute inset-0 rounded-sm border"
                style={{
                    borderColor: kind === 'new' ? '#a78bfa88' : '#2e323a',
                    background: `linear-gradient(180deg, ${baseColor}66 0%, ${baseColor}22 100%)`,
                }}
            />
            {/* U-slot lines */}
            {[0, 1, 2, 3, 4].map(i => (
                <div key={i} className="absolute left-[3px] right-[3px] h-px"
                    style={{ top: 7 + i * 9, background: '#00000044' }} />
            ))}
            {/* Status LED */}
            <div className="absolute top-1 right-1 w-1 h-1 rounded-full"
                style={{ background: accent, boxShadow: `0 0 4px ${accent}` }} />
            {/* Heat plume on overcapacity racks */}
            {kind === 'over' && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-1.5 h-3 rounded-full sim-heat"
                    style={{ background: 'radial-gradient(circle, #ef4444cc 0%, transparent 70%)' }} />
            )}
        </div>
    );
};

/* -----------------------------------------------------------------------
 * Flow Diagram (SVG)
 * --------------------------------------------------------------------- */
const FlowDiagram = ({
    facilityKw, coolingTons, pue, addedKw, isPwrCrit, isCoolCrit,
}: any) => {
    const pwrColor = isPwrCrit ? '#ef4444' : '#fbbf24';
    const coolColor = isCoolCrit ? '#ef4444' : '#38bdf8';
    return (
        <svg viewBox="0 0 600 240" className="w-full h-full">
            {/* Grid backdrop */}
            <defs>
                <pattern id="gridbg" width="30" height="30" patternUnits="userSpaceOnUse">
                    <path d="M30 0H0V30" stroke="#2e323a" strokeWidth="0.5" fill="none" />
                </pattern>
                <linearGradient id="pwrGrad" x1="0" x2="1" y1="0" y2="0">
                    <stop offset="0%" stopColor={pwrColor} stopOpacity="0.2" />
                    <stop offset="100%" stopColor={pwrColor} stopOpacity="1" />
                </linearGradient>
            </defs>
            <rect width="600" height="240" fill="url(#gridbg)" opacity="0.4" />

            {/* Utility / Grid block */}
            <g>
                <rect x="14" y="60" width="78" height="58" rx="6" fill="#14161b" stroke="#2e323a" />
                <text x="53" y="80" textAnchor="middle" fill="#a3a3a3" fontSize="10" fontWeight="bold">UTILITY</text>
                <text x="53" y="98" textAnchor="middle" fill="#fff" fontSize="11" fontWeight="bold">GRID</text>
                <text x="53" y="112" textAnchor="middle" fill="#86efac" fontSize="8">{BASE.renewablePct}% green</text>
            </g>

            {/* UPS */}
            <g>
                <rect x="140" y="60" width="78" height="58" rx="6" fill="#14161b" stroke="#2e323a" />
                <text x="179" y="78" textAnchor="middle" fill="#a3a3a3" fontSize="9" fontWeight="bold">UPS · 2N</text>
                <text x="179" y="96" textAnchor="middle" fill="#fff" fontSize="11" fontWeight="bold">{fmt(BASE.upsTotalKw)} kW</text>
                <circle cx="155" cy="108" r="2.5" fill="#22c55e" className="sim-pulse" />
                <circle cx="165" cy="108" r="2.5" fill="#22c55e" className="sim-pulse" />
            </g>

            {/* PDUs */}
            <g>
                <rect x="266" y="60" width="78" height="58" rx="6" fill="#14161b" stroke="#2e323a" />
                <text x="305" y="78" textAnchor="middle" fill="#a3a3a3" fontSize="9" fontWeight="bold">PDU BANK</text>
                <text x="305" y="96" textAnchor="middle" fill="#fff" fontSize="11" fontWeight="bold">32 cct.</text>
                <text x="305" y="110" textAnchor="middle" fill="#a3a3a3" fontSize="8">A+B feed</text>
            </g>

            {/* IT Load (racks block) */}
            <g>
                <rect x="392" y="40" width="190" height="100" rx="6" fill="#14161b" stroke={addedKw > 0 ? '#a78bfa66' : '#2e323a'} />
                <text x="487" y="60" textAnchor="middle" fill="#a3a3a3" fontSize="9" fontWeight="bold">IT LOAD · {BASE.racks + (addedKw > 0 ? Math.ceil(addedKw / 5) : 0)} RACKS</text>
                <text x="487" y="82" textAnchor="middle" fill="#fff" fontSize="16" fontWeight="bold">{fmt(facilityKw / pue, 1)} kW</text>
                <text x="487" y="98" textAnchor="middle" fill="#a3a3a3" fontSize="8">PUE {fmt(pue, 2)}</text>
                {/* mini rack icons */}
                <g transform="translate(420 108)">
                    {Array.from({ length: 8 }).map((_, i) => (
                        <rect key={i} x={i * 16} y={0} width={12} height={24} rx={1}
                            fill={i < 8 ? '#3f6f4f' : '#a78bfa'} opacity={0.8} />
                    ))}
                </g>
            </g>

            {/* Power lines (animated) */}
            <line x1="92" y1="89" x2="140" y2="89" stroke={pwrColor} strokeWidth="2.5" className="sim-pwr-line" />
            <line x1="218" y1="89" x2="266" y2="89" stroke={pwrColor} strokeWidth="2.5" className="sim-pwr-line" />
            <line x1="344" y1="89" x2="392" y2="89" stroke={pwrColor} strokeWidth="2.5" className="sim-pwr-line" />
            {/* arrow heads */}
            {[140, 266, 392].map((x, i) => (
                <polygon key={i} points={`${x - 5},85 ${x},89 ${x - 5},93`} fill={pwrColor} />
            ))}

            {/* CRAC unit (animated fan) */}
            <g>
                <rect x="180" y="170" width="120" height="58" rx="6" fill="#14161b" stroke="#2e323a" />
                <text x="240" y="188" textAnchor="middle" fill="#a3a3a3" fontSize="9" fontWeight="bold">CRAC · 4 UNITS N+1</text>
                <text x="240" y="206" textAnchor="middle" fill="#fff" fontSize="11" fontWeight="bold">{fmt(coolingTons, 1)} / {BASE.crackCapacityTons} tons</text>
                {/* Spinning fan icon */}
                <g transform="translate(294 197)">
                    <circle r="11" fill="#0a0c10" stroke={coolColor} strokeOpacity={0.5} />
                    <g className={isCoolCrit ? 'sim-fan-fast' : 'sim-fan'}>
                        <path d="M0 -8 C 3 -3 3 3 0 8 C -3 3 -3 -3 0 -8" fill={coolColor} opacity="0.85" />
                        <path d="M-8 0 C -3 -3 3 -3 8 0 C 3 3 -3 3 -8 0" fill={coolColor} opacity="0.6" />
                    </g>
                    <circle r="1.5" fill={coolColor} />
                </g>
            </g>

            {/* Cool air flow lines (rack -> CRAC return + CRAC -> rack supply) */}
            <path d="M 487 140 Q 487 165 300 199" stroke={coolColor} strokeWidth="2" fill="none" strokeOpacity="0.7" className="sim-cool-line" />
            <path d="M 180 199 Q 90 199 90 140 Q 90 110 392 110" stroke={coolColor} strokeWidth="2" fill="none" strokeOpacity="0.55" className="sim-cool-line" />
            <text x="350" y="155" fill={coolColor} fontSize="8" opacity="0.7">hot aisle return</text>
            <text x="100" y="135" fill={coolColor} fontSize="8" opacity="0.7">cold aisle supply</text>

            {/* Heat plume indicators when over capacity */}
            {isCoolCrit && (
                <g>
                    {[420, 460, 500, 540].map((x, i) => (
                        <circle key={i} cx={x} cy={32} r={3} fill="#ef4444"
                            className="sim-heat" style={{ animationDelay: `${i * 0.3}s` }} />
                    ))}
                </g>
            )}

            {/* Total facility label */}
            <text x="14" y="22" fill="#a3a3a3" fontSize="9" fontWeight="bold">FACILITY POWER</text>
            <text x="14" y="38" fill="#fff" fontSize="14" fontWeight="bold">{fmt(facilityKw, 1)} kW</text>

            <text x="586" y="22" textAnchor="end" fill="#a3a3a3" fontSize="9" fontWeight="bold">COOLING DELIVERED</text>
            <text x="586" y="38" textAnchor="end" fill="#fff" fontSize="14" fontWeight="bold">{fmt(coolingTons * 12000 / 1000, 0)}K BTU/hr</text>
        </svg>
    );
};

/* -----------------------------------------------------------------------
 * Main component
 * --------------------------------------------------------------------- */
const DataCenterScalingSimulator = () => {
    const [profile, setProfile] = useState<ProfileKey>('standard');
    const [addedRacks, setAddedRacks] = useState<number>(4);

    const P = PROFILES[profile];
    const MAX_ADD = 16;

    /* ---- Calculations ---- */
    const m = useMemo(() => {
        const addedItKw = addedRacks * P.kwPerRack;
        const newItKw = BASE.itLoadKw + addedItKw;

        // PUE degrades when cooling is stretched (until upgraded)
        // Each ton above 30 (75% of CRAC capacity) adds ~0.008 to PUE
        const newCoolingTons = BASE.itCoolingTons + addedItKw * KW_TO_TON;
        const overCool = Math.max(0, newCoolingTons - BASE.crackCapacityTons * 0.75);
        const newPue = BASE.pue + overCool * 0.008 + (profile === 'gpu' ? 0.04 : profile === 'highdensity' ? 0.015 : 0);

        const newFacilityKw = newItKw * newPue;
        const addedFacilityKw = newFacilityKw - BASE.facilityKw;

        const monthlyKwh = newFacilityKw * 24 * 30;
        const monthlyCost = monthlyKwh * BASE.costPerKwh;
        const monthlyCo2 = (monthlyKwh * BASE.co2PerKwh) / 1000;

        const addedMonthlyKwh = monthlyKwh - BASE.monthlyKwh;
        const addedMonthlyCost = monthlyCost - BASE.monthlyCost;
        const addedMonthlyCo2 = monthlyCo2 - BASE.monthlyCo2;

        const floorUsed = BASE.floorSqftUsed + addedRacks * SQFT_PER_NEW_RACK;
        const portsUsed = BASE.networkPortsUsed + addedRacks * PORTS_PER_NEW_RACK;
        const newRackCount = BASE.racks + addedRacks;

        const capexHardware = addedRacks * P.capexPerRack;

        return {
            addedItKw, newItKw, newPue, newFacilityKw, addedFacilityKw,
            newCoolingTons, addedCoolingTons: addedItKw * KW_TO_TON,
            addedCoolingBtu: addedItKw * KW_TO_BTU,
            monthlyKwh, monthlyCost, monthlyCo2,
            addedMonthlyKwh, addedMonthlyCost, addedMonthlyCo2,
            floorUsed, portsUsed, newRackCount, capexHardware,
        };
    }, [addedRacks, profile, P]);

    /* ---- Capacity utilisation percentages ---- */
    const upsPct = (m.newFacilityKw / BASE.upsTotalKw) * 100;
    const coolPct = (m.newCoolingTons / BASE.crackCapacityTons) * 100;
    const floorPct = (m.floorUsed / BASE.floorSqftTotal) * 100;
    // const netPct = (m.portsUsed / BASE.networkPortsTotal) * 100;

    /* ---- Risk register ---- */
    const risks = useMemo(() => {
        const list: { level: 'critical' | 'warn' | 'info' | 'ok'; title: string; detail: string; capex?: string }[] = [];

        if (upsPct >= 90) list.push({
            level: 'critical',
            title: 'UPS exceeds safe N+1 envelope',
            detail: `Facility load at ${fmt(upsPct, 0)}% of UPS capacity (${fmt(m.newFacilityKw, 0)} of ${BASE.upsTotalKw} kW). Loss of one module would cause downstream brown-out.`,
            capex: 'UPS expansion ≈ $45–65K (add 50 kVA module)',
        }); else if (upsPct >= 80) list.push({
            level: 'warn',
            title: 'UPS redundancy compromised',
            detail: `At ${fmt(upsPct, 0)}% utilisation the system can still ride through a single module failure but loses headroom for inrush during transfer events.`,
        });

        if (coolPct >= 90) list.push({
            level: 'critical',
            title: 'Cooling capacity insufficient',
            detail: `Sensible load at ${fmt(m.newCoolingTons, 1)} tons exceeds 90% of installed ${BASE.crackCapacityTons}-ton CRAC plant. Inlet temperatures will drift above ASHRAE A1 envelope on warm days.`,
            capex: 'Add 10-ton CRAC + piping ≈ $28–35K',
        }); else if (coolPct >= 75) list.push({
            level: 'warn',
            title: 'Cooling N+1 at risk in summer peaks',
            detail: `${fmt(coolPct, 0)}% utilisation leaves limited margin. During ambient highs the economiser cycle will run less, increasing kWh draw and PUE.`,
        });

        if (profile === 'gpu' && addedRacks >= 2) list.push({
            level: 'warn',
            title: 'High-density rack thermal profile',
            detail: `GPU racks at ${fmt(P.kwPerRack, 1)} kW exceed the 8 kW/rack design point. Hot-aisle containment and blanking panels are mandatory; consider rear-door heat exchangers for racks > 12 kW.`,
            capex: 'Containment + RDHX ≈ $14K/rack',
        });

        if (floorPct >= 95) list.push({
            level: 'critical',
            title: 'Floor space exhausted',
            detail: `${fmt(m.floorUsed)} of ${BASE.floorSqftTotal} sq ft used. Service clearances (36" front, 24" rear per NEC 110.26) cannot be maintained.`,
        }); else if (floorPct >= 85) list.push({
            level: 'warn',
            title: 'Restricted maintenance access',
            detail: `White-space utilisation at ${fmt(floorPct, 0)}%. Rack-swap operations will require staged moves.`,
        });

        if (m.portsUsed > BASE.networkPortsTotal) list.push({
            level: 'critical',
            title: 'ToR uplinks exhausted',
            detail: `${m.portsUsed} ports needed against ${BASE.networkPortsTotal} available. Add leaf switches or move to a spine-leaf fabric.`,
            capex: 'Leaf switch pair (48× 25G + 6× 100G) ≈ $24K',
        });

        if (m.addedItKw > 25 && upsPct < 80 && coolPct < 75) list.push({
            level: 'info',
            title: 'PDU branch circuit verification recommended',
            detail: `Adding ${fmt(m.addedItKw, 1)} kW IT load. Confirm 208V/30A or 415V/32A circuits are sized for the per-rack draw including spike factor.`,
        });

        if (m.newPue - BASE.pue >= 0.05) list.push({
            level: 'warn',
            title: 'PUE drift detected',
            detail: `Projected PUE ${fmt(m.newPue, 2)} vs baseline ${fmt(BASE.pue, 2)}. Driven by cooling-plant stretch and high-density losses. Mitigated by CRAC upgrade or in-row cooling.`,
        });

        if (list.length === 0) list.push({
            level: 'ok',
            title: 'All subsystems within design envelope',
            detail: 'Expansion can proceed without infrastructure upgrades. Standard commissioning + burn-in window of 7 days recommended before production cutover.',
        });

        return list;
    }, [upsPct, coolPct, floorPct, m, profile, addedRacks, P.kwPerRack]);

    /* ---- 12-month deployment projection (phased: 1 rack/wk to ramp) ---- */
    const projection = useMemo(() => {
        // Linear ramp: deploy added racks over first 4 months, hold steady after
        const months = ['M1', 'M2', 'M3', 'M4', 'M5', 'M6', 'M7', 'M8', 'M9', 'M10', 'M11', 'M12'];
        return months.map((label, i) => {
            const ratio = Math.min(1, (i + 1) / 4);
            const racksAddedSoFar = addedRacks * ratio;
            const itKw = BASE.itLoadKw + racksAddedSoFar * P.kwPerRack;
            const tons = BASE.itCoolingTons + racksAddedSoFar * P.kwPerRack * KW_TO_TON;
            const pue = BASE.pue
                + Math.max(0, (tons - BASE.crackCapacityTons * 0.75)) * 0.008
                + (profile === 'gpu' ? 0.04 : profile === 'highdensity' ? 0.015 : 0) * ratio;
            const facKw = itKw * pue;
            return {
                month: label,
                itKw: +itKw.toFixed(1),
                facKw: +facKw.toFixed(1),
                coolingTons: +tons.toFixed(1),
                cost: +(facKw * 24 * 30 * BASE.costPerKwh).toFixed(0),
            };
        });
    }, [addedRacks, profile, P]);

    /* ---- Subsystem comparison data ---- */
    const subsystemData = [
        { name: 'Power', baseline: BASE.facilityKw, projected: m.newFacilityKw, limit: BASE.upsTotalKw, unit: 'kW' },
        { name: 'Cooling', baseline: BASE.itCoolingTons, projected: m.newCoolingTons, limit: BASE.crackCapacityTons, unit: 'tons' },
        { name: 'Floor', baseline: BASE.floorSqftUsed, projected: m.floorUsed, limit: BASE.floorSqftTotal, unit: 'sqft' },
        { name: 'Net Ports', baseline: BASE.networkPortsUsed, projected: m.portsUsed, limit: BASE.networkPortsTotal, unit: 'ports' },
    ];

    /* ---- Rack tiles ---- */
    const rackTiles = useMemo(() => {
        const list: { kw: number; kind: 'existing' | 'new' | 'over' }[] = [];
        for (let i = 0; i < BASE.racks; i++) list.push({ kw: BASE.avgKwPerRack, kind: 'existing' });
        for (let i = 0; i < addedRacks; i++) {
            const isOver = (upsPct > 100 && i >= addedRacks - 2) || coolPct > 100;
            list.push({ kw: P.kwPerRack, kind: isOver ? 'over' : 'new' });
        }
        return list;
    }, [addedRacks, P.kwPerRack, upsPct, coolPct]);

    const worstRisk = risks.find(r => r.level === 'critical') ? 'critical'
        : risks.find(r => r.level === 'warn') ? 'warn'
            : risks.find(r => r.level === 'info') ? 'info' : 'ok';

    /* ---- Render ---- */
    return (
        <div className="w-full h-full flex flex-col gap-4 text-white pb-10">
            <style>{animStyles}</style>

            {/* Header strip */}
            <div className="flex items-center justify-between gap-4 bg-[#1a1d24] border border-[#2e323a] rounded-lg p-4">
                <div className="flex items-center gap-3">
                    <div className="p-2 rounded-md bg-violet-500/15 text-violet-400">
                        <Layers size={18} />
                    </div>
                    <div>
                        <div className="text-sm font-bold">Data Center Scaling Simulator</div>
                        <div className="text-[10px] text-zinc-500">
                            Baseline: {BASE.racks} racks · {fmt(BASE.itLoadKw)} kW IT · PUE {fmt(BASE.pue, 2)} · {BASE.renewablePct}% renewable
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <span className={`text-[10px] font-bold px-2 py-1 rounded ${worstRisk === 'critical' ? 'bg-red-500/20 text-red-400' :
                            worstRisk === 'warn' ? 'bg-orange-500/20 text-orange-400' :
                                worstRisk === 'info' ? 'bg-blue-500/20 text-blue-400' :
                                    'bg-green-500/20 text-green-400'
                        }`}>
                        {worstRisk === 'ok' ? 'READY TO DEPLOY' : worstRisk === 'info' ? 'REVIEW REQUIRED' : worstRisk === 'warn' ? 'WARNINGS' : 'BLOCKERS'}
                    </span>
                </div>
            </div>

            {/* Top metrics row */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                <MetricCard
                    icon={<Server size={12} />}
                    label="Total Racks"
                    value={m.newRackCount}
                    sub={`${BASE.racks} existing + ${addedRacks} new`}
                    delta={addedRacks > 0 ? `+${addedRacks}` : '—'}
                    deltaTone={addedRacks > 0 ? 'good' : 'neutral'}
                />
                <MetricCard
                    icon={<Zap size={12} />}
                    label="Facility Power"
                    value={`${fmt(m.newFacilityKw, 1)} kW`}
                    sub={`IT ${fmt(m.newItKw, 0)} kW · PUE ${fmt(m.newPue, 2)}`}
                    delta={`+${fmt(m.addedFacilityKw, 1)} kW`}
                    deltaTone={upsPct >= 90 ? 'critical' : upsPct >= 80 ? 'warn' : 'good'}
                />
                <MetricCard
                    icon={<Snowflake size={12} />}
                    label="Cooling Load"
                    value={`${fmt(m.newCoolingTons, 1)} tons`}
                    sub={`${fmt(m.newCoolingTons * 12000 / 1000, 0)}K BTU/hr · CRAC ${BASE.crackCapacityTons}-ton`}
                    delta={`+${fmt(m.addedCoolingTons, 1)} t`}
                    deltaTone={coolPct >= 90 ? 'critical' : coolPct >= 75 ? 'warn' : 'good'}
                />
                <MetricCard
                    icon={<DollarSign size={12} />}
                    label="Monthly Energy"
                    value={`$${fmt(m.monthlyCost, 0)}`}
                    sub={`${fmt(m.monthlyKwh / 1000, 1)}K kWh @ $${BASE.costPerKwh.toFixed(2)}/kWh`}
                    delta={`+$${fmt(m.addedMonthlyCost, 0)}/mo`}
                    deltaTone="warn"
                />
                <MetricCard
                    icon={<Leaf size={12} />}
                    label="Monthly CO₂"
                    value={`${fmt(m.monthlyCo2, 2)} t`}
                    sub={`${BASE.co2PerKwh.toFixed(3)} kg/kWh grid factor`}
                    delta={`+${fmt(m.addedMonthlyCo2, 2)} t`}
                    deltaTone="warn"
                />
                <MetricCard
                    icon={<Gauge size={12} />}
                    label="Hardware CapEx"
                    value={`$${fmt(m.capexHardware / 1000, 1)}K`}
                    sub={`${P.name} · $${fmt(P.capexPerRack / 1000, 0)}K/rack`}
                    delta={addedRacks > 0 ? 'one-time' : '—'}
                    deltaTone="neutral"
                />
            </div>

            {/* Configure + Flow diagram */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* CONFIGURE */}
                <div className="bg-[#1a1d24] border border-[#2e323a] rounded-lg p-5 flex flex-col gap-4">
                    <div className="flex items-center gap-2">
                        <div className="w-1.5 h-4 bg-violet-500 rounded-sm" />
                        <h3 className="font-bold text-sm">Configure Expansion</h3>
                    </div>

                    {/* Rack profile picker */}
                    <div>
                        <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-2">Rack profile</div>
                        <div className="flex flex-col gap-2">
                            {(Object.keys(PROFILES) as ProfileKey[]).map(k => {
                                const p = PROFILES[k];
                                const active = profile === k;
                                return (
                                    <button
                                        key={k}
                                        onClick={() => setProfile(k)}
                                        className={`text-left px-3 py-2 rounded border sim-transition flex items-center justify-between ${active ? 'bg-[#14161b] border-violet-500/50' : 'bg-[#14161b]/40 border-[#2e323a] hover:border-zinc-600'
                                            }`}
                                        style={active ? { boxShadow: `0 0 0 1px ${p.color}55` } : {}}
                                    >
                                        <div className="flex items-center gap-2">
                                            <div className="w-2 h-8 rounded-sm" style={{ background: p.color }} />
                                            <div>
                                                <div className="text-xs font-bold text-white">{p.name}</div>
                                                <div className="text-[9px] text-zinc-500">{p.sub}</div>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-xs font-bold text-white">{fmt(p.kwPerRack, 1)} kW</div>
                                            <div className="text-[9px] text-zinc-500">${fmt(p.capexPerRack / 1000)}K</div>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Rack count slider */}
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <div className="text-[10px] text-zinc-500 uppercase tracking-wider">Add racks</div>
                            <div className="text-[10px] text-zinc-500">max {MAX_ADD}</div>
                        </div>
                        <div className="flex items-center gap-3">
                            <button
                                onClick={() => setAddedRacks(Math.max(0, addedRacks - 1))}
                                className="w-8 h-8 rounded bg-[#14161b] border border-[#2e323a] hover:border-zinc-500 flex items-center justify-center"
                            >
                                <Minus size={14} />
                            </button>
                            <div className="flex-1 relative">
                                <input
                                    type="range" min={0} max={MAX_ADD} step={1}
                                    value={addedRacks}
                                    onChange={(e) => setAddedRacks(parseInt(e.target.value, 10))}
                                    className="w-full accent-violet-500"
                                />
                                <div className="text-center text-2xl font-bold text-white sim-transition">
                                    {addedRacks}
                                </div>
                            </div>
                            <button
                                onClick={() => setAddedRacks(Math.min(MAX_ADD, addedRacks + 1))}
                                className="w-8 h-8 rounded bg-[#14161b] border border-[#2e323a] hover:border-zinc-500 flex items-center justify-center"
                            >
                                <Plus size={14} />
                            </button>
                        </div>
                        <div className="text-[10px] text-zinc-500 mt-1 text-center">
                            adds <span className="text-white font-bold">{fmt(m.addedItKw, 1)} kW</span> IT ·
                            <span className="text-white font-bold"> {fmt(m.addedCoolingTons, 1)} tons</span> cooling
                        </div>
                    </div>

                    {/* Quick presets */}
                    <div className="flex gap-2">
                        {[
                            { label: '+2', val: 2 },
                            { label: '+4', val: 4 },
                            { label: '+8', val: 8 },
                            { label: 'Max', val: MAX_ADD },
                        ].map(p => (
                            <button
                                key={p.label}
                                onClick={() => setAddedRacks(p.val)}
                                className="flex-1 text-[10px] py-1.5 rounded border border-[#2e323a] bg-[#14161b] hover:border-violet-500/50 transition"
                            >
                                {p.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* FLOW DIAGRAM */}
                <div className="bg-[#1a1d24] border border-[#2e323a] rounded-lg p-5 col-span-1 lg:col-span-2 flex flex-col">
                    <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                            <div className="w-1.5 h-4 bg-amber-400 rounded-sm" />
                            <h3 className="font-bold text-sm">Live Power & Cooling Flow</h3>
                        </div>
                        <div className="flex items-center gap-3 text-[10px]">
                            <span className="flex items-center gap-1 text-amber-400">
                                <span className="w-2 h-2 rounded-full bg-amber-400 sim-pulse" />
                                Power
                            </span>
                            <span className="flex items-center gap-1 text-sky-400">
                                <span className="w-2 h-2 rounded-full bg-sky-400 sim-pulse" />
                                Cooling
                            </span>
                        </div>
                    </div>
                    <div className="flex-1 min-h-[240px]">
                        <FlowDiagram
                            facilityKw={m.newFacilityKw}
                            coolingTons={m.newCoolingTons}
                            pue={m.newPue}
                            addedKw={m.addedItKw}
                            isPwrCrit={upsPct >= 90}
                            isCoolCrit={coolPct >= 90}
                        />
                    </div>
                </div>
            </div>

            {/* Rack grid + capacity gauges */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Rack grid */}
                <div className="bg-[#1a1d24] border border-[#2e323a] rounded-lg p-5 col-span-1 lg:col-span-2">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                            <div className="w-1.5 h-4 bg-emerald-500 rounded-sm" />
                            <h3 className="font-bold text-sm">Rack Floorplan · Row A & B</h3>
                        </div>
                        <div className="flex items-center gap-3 text-[10px]">
                            <span className="flex items-center gap-1.5">
                                <span className="w-2 h-2 rounded-sm bg-green-500" />
                                <span className="text-zinc-400">Existing ({BASE.racks})</span>
                            </span>
                            <span className="flex items-center gap-1.5">
                                <span className="w-2 h-2 rounded-sm bg-violet-400 sim-pulse" />
                                <span className="text-zinc-400">New ({addedRacks})</span>
                            </span>
                            <span className="flex items-center gap-1.5">
                                <span className="w-2 h-2 rounded-sm bg-red-500" />
                                <span className="text-zinc-400">Over-cap</span>
                            </span>
                        </div>
                    </div>

                    <div className="bg-[#0e1014] border border-[#2e323a] rounded p-4">
                        {/* Row A label */}
                        <div className="text-[9px] text-zinc-600 uppercase tracking-wider mb-1.5">Row A · cold aisle ↑</div>
                        <div className="flex flex-wrap gap-1.5 mb-4">
                            {rackTiles.slice(0, 8).map((r, i) => (
                                <RackTile key={`a-${i}`} kw={r.kw} kind={r.kind} idx={i} />
                            ))}
                            {Array.from({ length: Math.max(0, 8 - rackTiles.slice(0, 8).length) }).map((_, i) => (
                                <div key={`a-empty-${i}`} className="rounded-sm border border-dashed border-[#2e323a]" style={{ width: 26, height: 56 }} />
                            ))}
                        </div>
                        <div className="text-[9px] text-zinc-600 uppercase tracking-wider mb-1.5">Row B · hot aisle ↓</div>
                        <div className="flex flex-wrap gap-1.5 mb-2">
                            {rackTiles.slice(8, 16).map((r, i) => (
                                <RackTile key={`b-${i}`} kw={r.kw} kind={r.kind} idx={i + 8} />
                            ))}
                            {Array.from({ length: Math.max(0, 8 - rackTiles.slice(8, 16).length) }).map((_, i) => (
                                <div key={`b-empty-${i}`} className="rounded-sm border border-dashed border-[#2e323a]" style={{ width: 26, height: 56 }} />
                            ))}
                        </div>
                        {/* Expansion row (Row C) */}
                        {rackTiles.length > 16 && (
                            <>
                                <div className="text-[9px] text-violet-400 uppercase tracking-wider mb-1.5 mt-3 border-t border-dashed border-violet-500/30 pt-3">
                                    Row C · planned expansion
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                    {rackTiles.slice(16).map((r, i) => (
                                        <RackTile key={`c-${i}`} kw={r.kw} kind={r.kind} idx={i + 16} />
                                    ))}
                                </div>
                            </>
                        )}

                        <div className="grid grid-cols-4 gap-3 mt-5 pt-3 border-t border-[#2e323a]">
                            <div>
                                <div className="text-[9px] text-zinc-500 uppercase">Total Racks</div>
                                <div className="text-sm font-bold text-white sim-transition">{m.newRackCount} / 32</div>
                            </div>
                            <div>
                                <div className="text-[9px] text-zinc-500 uppercase">Avg Density</div>
                                <div className="text-sm font-bold text-white sim-transition">{fmt(m.newItKw / m.newRackCount, 1)} kW/rack</div>
                            </div>
                            <div>
                                <div className="text-[9px] text-zinc-500 uppercase">Floor Used</div>
                                <div className="text-sm font-bold text-white sim-transition">{fmt(m.floorUsed)} sqft</div>
                            </div>
                            <div>
                                <div className="text-[9px] text-zinc-500 uppercase">Network Ports</div>
                                <div className="text-sm font-bold text-white sim-transition">{m.portsUsed} / {BASE.networkPortsTotal}</div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Capacity gauges */}
                <div className="bg-[#1a1d24] border border-[#2e323a] rounded-lg p-5 flex flex-col gap-3">
                    <div className="flex items-center gap-2 mb-1">
                        <div className="w-1.5 h-4 bg-red-400 rounded-sm" />
                        <h3 className="font-bold text-sm">Subsystem Headroom</h3>
                    </div>
                    <div className="grid grid-cols-1 gap-3 flex-1">
                        <CapacityBar
                            icon={<Zap size={11} className="text-amber-400" />}
                            label="UPS / Power"
                            used={m.newFacilityKw}
                            capacity={BASE.upsTotalKw}
                            unit="kW"
                            warnPct={80}
                            critPct={90}
                        />
                        <CapacityBar
                            icon={<Snowflake size={11} className="text-sky-400" />}
                            label="CRAC Cooling"
                            used={m.newCoolingTons}
                            capacity={BASE.crackCapacityTons}
                            unit="tons"
                            warnPct={75}
                            critPct={90}
                        />
                        <CapacityBar
                            icon={<Layers size={11} className="text-emerald-400" />}
                            label="White Space"
                            used={m.floorUsed}
                            capacity={BASE.floorSqftTotal}
                            unit="sqft"
                            warnPct={85}
                            critPct={95}
                        />
                        <CapacityBar
                            icon={<Network size={11} className="text-violet-400" />}
                            label="Network Ports"
                            used={m.portsUsed}
                            capacity={BASE.networkPortsTotal}
                            unit="ports"
                            warnPct={80}
                            critPct={95}
                        />
                    </div>
                </div>
            </div>

            {/* Subsystem chart + Risk register */}
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
                {/* Subsystem before/after chart */}
                <div className="bg-[#1a1d24] border border-[#2e323a] rounded-lg p-5 col-span-1 lg:col-span-2 flex flex-col min-h-[320px]">
                    <div className="flex items-center gap-2 mb-3">
                        <div className="w-1.5 h-4 bg-sky-400 rounded-sm" />
                        <h3 className="font-bold text-sm">Subsystem Load · Baseline vs Projected</h3>
                    </div>
                    <div className="flex-1 w-full -ml-3">
                        <ResponsiveContainer width="100%" height="100%">
                            <ComposedChart data={subsystemData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#2e323a" vertical={false} />
                                <XAxis dataKey="name" stroke="#71717a" fontSize={10} tickLine={false} axisLine={false} />
                                <YAxis stroke="#71717a" fontSize={10} tickLine={false} axisLine={false} />
                                <Tooltip content={<CustomTooltip />} />
                                <Legend wrapperStyle={{ fontSize: '10px' }} verticalAlign="top" iconType="circle" />
                                <Bar name="Baseline" dataKey="baseline" fill="#3f6f4f" radius={[2, 2, 0, 0]} barSize={22} />
                                <Bar name="Projected" dataKey="projected" radius={[2, 2, 0, 0]} barSize={22}>
                                    {subsystemData.map((d, i) => {
                                        const pct = (d.projected / d.limit) * 100;
                                        const c = pct >= 90 ? '#ef4444' : pct >= 75 ? '#f97316' : '#a78bfa';
                                        return <Cell key={i} fill={c} />;
                                    })}
                                </Bar>
                                <Line name="Capacity Limit" dataKey="limit" stroke="#fbbf24" strokeWidth={1.5} strokeDasharray="4 4" dot={{ r: 3, fill: '#fbbf24' }} />
                            </ComposedChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Risk register */}
                <div className="bg-[#1a1d24] border border-[#2e323a] rounded-lg p-5 col-span-1 lg:col-span-3 flex flex-col">
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                            <div className="w-1.5 h-4 bg-orange-400 rounded-sm" />
                            <h3 className="font-bold text-sm">Risk Register & Engineering Notes</h3>
                        </div>
                        <span className="text-[10px] text-zinc-500">{risks.length} item{risks.length !== 1 ? 's' : ''}</span>
                    </div>

                    <div className="space-y-2 overflow-y-auto pr-1" style={{ maxHeight: 360 }}>
                        {risks.map((r, i) => {
                            const cfg =
                                r.level === 'critical' ? { color: '#ef4444', bg: 'bg-red-500/10', border: 'border-red-500/40', Icon: ShieldAlert, label: 'CRITICAL' } :
                                    r.level === 'warn' ? { color: '#f97316', bg: 'bg-orange-500/10', border: 'border-orange-500/40', Icon: AlertTriangle, label: 'WARNING' } :
                                        r.level === 'info' ? { color: '#38bdf8', bg: 'bg-sky-500/10', border: 'border-sky-500/40', Icon: Info, label: 'NOTICE' } :
                                            { color: '#22c55e', bg: 'bg-green-500/10', border: 'border-green-500/40', Icon: CheckCircle2, label: 'OK' };
                            const { Icon } = cfg;
                            return (
                                <div key={i} className={`${cfg.bg} ${cfg.border} border rounded-md p-3 flex gap-3 items-start sim-transition`}>
                                    <Icon size={16} style={{ color: cfg.color, flexShrink: 0, marginTop: 2 }} />
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded"
                                                style={{ background: `${cfg.color}22`, color: cfg.color }}>
                                                {cfg.label}
                                            </span>
                                            <span className="text-xs font-bold text-white">{r.title}</span>
                                        </div>
                                        <p className="text-[11px] text-zinc-400 leading-relaxed">{r.detail}</p>
                                        {r.capex && (
                                            <div className="mt-1.5 text-[10px] text-amber-400 flex items-center gap-1">
                                                <DollarSign size={10} />
                                                {r.capex}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* 12-month deployment projection */}
            {/* 12-month deployment projection */}
            <div className="bg-[#1a1d24] border border-[#2e323a] rounded-lg p-5 min-h-[300px] flex flex-col">
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                        <div className="w-1.5 h-4 bg-violet-400 rounded-sm" />
                        <h3 className="font-bold text-sm">12-Month Deployment Trajectory</h3>
                        <span className="text-[10px] text-zinc-500 ml-2">phased ramp over 4 months, then steady-state</span>
                    </div>
                    <div className="flex items-center gap-4 text-[10px]">
                        <div>
                            <span className="text-zinc-500">Year-1 Δ energy: </span>
                            <span className="font-bold text-white">${fmt(m.addedMonthlyCost * 12, 0)}</span>
                        </div>
                        <div>
                            <span className="text-zinc-500">Year-1 Δ CO₂: </span>
                            <span className="font-bold text-white">{fmt(m.addedMonthlyCo2 * 12, 1)} t</span>
                        </div>
                    </div>
                </div>

                <div className="w-full h-[260px] -ml-3">
                    <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart data={projection} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#2e323a" vertical={false} />
                            <XAxis dataKey="month" stroke="#71717a" fontSize={10} tickLine={false} axisLine={false} />
                            <YAxis
                                yAxisId="left"
                                stroke="#71717a"
                                fontSize={10}
                                tickLine={false}
                                axisLine={false}
                                label={{ value: 'kW', angle: -90, position: 'insideLeft', fill: '#71717a', fontSize: 10 }}
                            />
                            <YAxis
                                yAxisId="right"
                                orientation="right"
                                stroke="#71717a"
                                fontSize={10}
                                tickLine={false}
                                axisLine={false}
                                label={{ value: 'tons', angle: 90, position: 'insideRight', fill: '#71717a', fontSize: 10 }}
                            />
                            <Tooltip content={<CustomTooltip />} />
                            <Legend wrapperStyle={{ fontSize: '10px' }} verticalAlign="top" iconType="circle" />
                            <ReferenceLine
                                yAxisId="left"
                                y={BASE.upsTotalKw}
                                stroke="#ef4444"
                                strokeDasharray="4 4"
                                label={{
                                    value: `UPS limit ${BASE.upsTotalKw} kW`,
                                    fill: '#ef4444',
                                    fontSize: 9,
                                    position: 'insideTopRight'
                                }}
                            />
                            <Bar yAxisId="left" name="IT Load (kW)" dataKey="itKw" fill="#60a5fa" radius={[2, 2, 0, 0]} barSize={14} />
                            <Bar yAxisId="left" name="Facility (kW)" dataKey="facKw" fill="#a78bfa" radius={[2, 2, 0, 0]} barSize={14} />
                            <Line
                                yAxisId="right"
                                name="Cooling (tons)"
                                type="monotone"
                                dataKey="coolingTons"
                                stroke="#38bdf8"
                                strokeWidth={2}
                                dot={{ r: 3, fill: '#38bdf8' }}
                            />
                        </ComposedChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );
};

export default DataCenterScalingSimulator;
